import React, { useState, useEffect } from 'react';
import { X, Clock, Edit2, Save, AlertCircle } from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import './DurationModal.css';
import api from '../services/api';

interface StatusDuration {
  status: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

interface DurationModalProps {
  appointmentId: string;
  patientName: string;
  onClose: () => void;
  onUpdate: () => void;
}

const DurationModal: React.FC<DurationModalProps> = ({
  appointmentId,
  patientName,
  onClose,
  onUpdate
}) => {
  const [durations, setDurations] = useState<StatusDuration[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [manualDuration, setManualDuration] = useState<string>('');
  const [durationNote, setDurationNote] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [currentAppointment, setCurrentAppointment] = useState<any>(null);

  useEffect(() => {
    fetchDurations();
    fetchAppointment();
  }, [appointmentId]);

  const fetchDurations = async () => {
    try {
      const response = await api.get(`/api/appointments/${appointmentId}/durations`);

      if (response.data) {
        setDurations(response.data);
      }
    } catch (error) {
      console.error('Error fetching durations:', error);
      setDurations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointment = async () => {
    try {
      const response = await api.get('/api/appointments');

      if (response.data) {
        const appointment = response.data.find((apt: any) => apt.id === appointmentId);
        if (appointment) {
          setCurrentAppointment(appointment);
          if (appointment.manual_duration_override) {
            setManualDuration(appointment.manual_duration_override.toString());
            setDurationNote(appointment.manual_duration_note || '');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
    }
  };

  const formatStatus = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'not_arrived': 'Not Arrived',
      'lobby': 'Lobby',
      'in_room': 'Ready CRC',
      'with_crc': 'w/CRC',
      'waiting_on_inv': 'Waiting on INV',
      'with_investigator': 'w/Investigator',
      'done': 'Done'
    };
    return statusLabels[status] || status;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getTotalDuration = (): number => {
    // If manual override exists, use it
    if (currentAppointment?.manual_duration_override) {
      return currentAppointment.manual_duration_override;
    }
    
    // IMPORTANT: Calculate based on check_in_time and check_out_time for completed visits
    if (currentAppointment) {
      const checkInTime = currentAppointment.check_in_time;
      const checkOutTime = currentAppointment.check_out_time;
      const status = currentAppointment.patient_status;
      
      if (!checkInTime) {
        // No check-in time, can't calculate
        return 0;
      }
      
      // If patient is done and has check_out_time, use that as end time
      if (status === 'done' && checkOutTime) {
        return differenceInMinutes(new Date(checkOutTime), new Date(checkInTime));
      }
      
      // If patient is still active (not done), calculate to current time
      if (status !== 'done') {
        return differenceInMinutes(new Date(), new Date(checkInTime));
      }
      
      // Fallback for done patients without check_out_time (legacy data)
      // Use the last status change time if available
      if (status === 'done' && !checkOutTime && durations.length > 0) {
        const lastDuration = durations[durations.length - 1];
        if (lastDuration.end_time) {
          return differenceInMinutes(new Date(lastDuration.end_time), new Date(checkInTime));
        }
      }
    }
    
    // Final fallback: sum all durations
    return durations.reduce((total, d) => total + d.duration_minutes, 0);
  };

  const handleSaveManualDuration = async () => {
    try {
      const response = await api.patch(
        `/api/appointments/${appointmentId}/duration`,
        {
          manual_duration_override: manualDuration ? parseInt(manualDuration) : null,
          manual_duration_note: durationNote
        }
      );

      if (response.status === 200) {
        setIsEditing(false);
        onUpdate();
        fetchAppointment();
      }
    } catch (error) {
      console.error('Error saving manual duration:', error);
      alert('Failed to save manual duration');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="duration-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Visit Duration Details</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="patient-info">
            <h3>{patientName}</h3>
            <div className="total-duration">
              <Clock size={16} />
              <span>Total Duration: </span>
              <strong>{formatDuration(getTotalDuration())}</strong>
              {currentAppointment?.patient_status === 'done' && (
                <span style={{ color: '#28a745', marginLeft: '10px' }}>
                  ✓ Completed
                </span>
              )}
              {currentAppointment?.manual_duration_override && (
                <span className="manual-indicator">
                  <AlertCircle size={14} />
                  Manually adjusted
                </span>
              )}
            </div>
            {currentAppointment?.patient_status === 'done' && currentAppointment?.check_out_time && (
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>
                Checked out at: {format(new Date(currentAppointment.check_out_time), 'h:mm a')}
              </div>
            )}
          </div>

          <div className="duration-breakdown">
            <h4>Status Breakdown</h4>
            {loading ? (
              <div className="loading">Loading...</div>
            ) : durations.length > 0 ? (
              <table className="duration-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {durations.map((duration, index) => (
                    <tr key={index}>
                      <td>
                        <span className={`status-badge status-${duration.status.replace('_', '-')}`}>
                          {formatStatus(duration.status)}
                        </span>
                      </td>
                      <td>{format(new Date(duration.start_time), 'h:mm a')}</td>
                      <td>
                        {duration.end_time ? 
                          format(new Date(duration.end_time), 'h:mm a') : 
                          'Current'
                        }
                      </td>
                      <td>{formatDuration(duration.duration_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No status history available</p>
            )}
          </div>

          <div className="manual-override">
            <div className="override-header">
              <h4>Manual Duration Override</h4>
              {!isEditing && (
                <button 
                  className="edit-btn"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 size={16} />
                  Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Total Duration (minutes)</label>
                  <input
                    type="number"
                    value={manualDuration}
                    onChange={(e) => setManualDuration(e.target.value)}
                    placeholder="Leave empty to use calculated duration"
                  />
                </div>
                <div className="form-group">
                  <label>Note (optional)</label>
                  <textarea
                    value={durationNote}
                    onChange={(e) => setDurationNote(e.target.value)}
                    placeholder="Reason for manual adjustment..."
                    rows={3}
                  />
                </div>
                <div className="form-actions">
                  <button 
                    className="cancel-btn"
                    onClick={() => {
                      setIsEditing(false);
                      setManualDuration(currentAppointment?.manual_duration_override?.toString() || '');
                      setDurationNote(currentAppointment?.manual_duration_note || '');
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="save-btn"
                    onClick={handleSaveManualDuration}
                  >
                    <Save size={16} />
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="override-display">
                {currentAppointment?.manual_duration_override ? (
                  <>
                    <p><strong>Override:</strong> {formatDuration(currentAppointment.manual_duration_override)}</p>
                    {currentAppointment.manual_duration_note && (
                      <p><strong>Note:</strong> {currentAppointment.manual_duration_note}</p>
                    )}
                  </>
                ) : (
                  <p className="no-override">No manual override set</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DurationModal;